import { useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { axios } from "setups/axios";

import { validateNewPassword } from "./validatePassword";

const modalStyle = {
  width: "90%",
  maxWidth: "420px",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: (theme) => `1px solid ${theme.palette.divider}`,
  boxShadow: 24,
  p: 3,
  textAlign: "left",
};

const ChangeExpiredPasswordModal = ({
  open,
  SAP_ID,
  currentPin,
  onSuccess,
}) => {
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setNewPin("");
    setConfirmPin("");
    setError("");
  };

  const handleSubmit = async () => {
    const validationError = validateNewPassword(newPin);

    if (validationError) {
      setError(validationError);
      return;
    }

    if (newPin !== confirmPin) {
      setError("New password and confirm password do not match.");
      return;
    }

    if (newPin === currentPin) {
      setError("New password must be different from your current password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios()({
        url: `/api/v1/admin/change-expired-password`,
        method: "POST",
        data: {
          SAP_ID,
          currentPin,
          newPin,
        },
      });

      const { data } = res.data;

      if (data?.token) {
        localStorage.setItem("token", data.token);
        resetForm();
        onSuccess?.(data);
        location.reload();
        return;
      }

      setError("Password update failed. Please try again.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to update password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      aria-labelledby="change-expired-password-title"
      disableEscapeKeyDown
    >
      <Box sx={modalStyle}>
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Typography
              id="change-expired-password-title"
              variant="h6"
              sx={{ fontWeight: 700 }}
            >
              Create New Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your password has expired. Minimum 8 characters with at least
              three of: uppercase, lowercase, numbers, and special characters.
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            id="new-pin"
            fullWidth
            type="password"
            label="New Password"
            placeholder="New Password"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            id="confirm-pin"
            fullWidth
            type="password"
            label="Confirm New Password"
            placeholder="Confirm New Password"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            id="change-password-submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading}
            onClick={handleSubmit}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ fontWeight: 700 }}
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default ChangeExpiredPasswordModal;
